import { useContext, useEffect, useState } from "react";
import { BiPlus } from "react-icons/bi";
import API from "../../../utils/API";
import { useProject } from "../../../utils/hooks/useProject";
import Modal from "../../../utils/components/Modal";
import Input from "../../../utils/components/Input";
import { useOutletContext } from "react-router";
import { Helmet } from "react-helmet-async";
import Sets from "./Sets";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../../utils/components/AlertModal";
import { RequestContext } from "../../../utils/context/RequestContext";

const LocationSets = () => {
  const [locationSets, setLocationSets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [locationSetName, setLocationSetName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchCompleted, setIsFetchCompleted] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const { project } = useProject();

  const [translate] = useOutletContext();

  const { setRequestLoading, requestLoading } = useContext(RequestContext);

  useEffect(() => {
    if (project?.id)
      API.getData(`site_sets?project_id=${project.id}`).then(
        ({ data: res }) => {
          setTimeout(() => {
            const data = res.data.map((item) => {
              return {
                id: item.id,
                title: item.name,
                subTree: [],
                hasDetail: item.is_details,
              };
            });

            setLocationSets(data);
          }, 20);

          setTimeout(() => {
            setIsFetchCompleted(true);
          }, 50);
        }
      );
    else setIsFetchCompleted(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFetchCompleted) handleGetLocationSetsDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchCompleted]);

  const handleGetLocationSetsDetails = () => {
    if (locationSets.length > 0) {
      locationSets.forEach((set) => {
        if (set.hasDetail) {
          API.getData(`site_set/${set.id}`).then(({ data: res }) => {
            const sets = [...locationSets];

            const data = res.data;

            const index = sets.findIndex((item) => item.id === data.id);

            if (index > -1) {
              sets[index].subTree = data.details.map((detail) => ({
                id: detail.id,
                name: detail.name,
                hasDetail: detail.is_details,
                subTree: [],
                set_id: set.id,
              }));
              setLocationSets(sets);
            }
          });
        }

        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (requestLoading) return;

    if (!locationSetName.trim() || !project) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate("fill_all_fields")}
          />
        ),
      });
      return false;
    }

    setIsSending(true);
    setRequestLoading(true);

    const body = {
      name: locationSetName,
    };

    API.postData(`site_sets?project_id=${project.id}`, body)
      .then(({ data: res }) => {
        const { data } = res;

        const newItem = {
          id: data.id,
          title: data.name.trim(),
          subTree: [],
        };

        setLocationSets([...locationSets, newItem]);

        setShowModal(false);
        setLocationSetName("");
        setIsSending(false);
      })
      .catch(() => {
        setIsSending(false);
      });
  };

  const onDelete = (id) => {
    if (!id) return false;

    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                handleDelete(id, onClose);
              },
              classNames: "btn btn-danger",
            },
          ]}
          message={translate("delete_confirmation")}
        />
      ),
    });
  };

  const handleDelete = (id, closeModal) => {
    if (requestLoading) return;

    setRequestLoading(true);

    API.deleteData(`site_set/${id}`).then(() => {
      const newLocationSets = locationSets.filter((item) => item.id !== id);
      setLocationSets(newLocationSets);
      closeModal();

      let locationRoot = localStorage.getItem("selected-forms-location-root");
      locationRoot = locationRoot ? JSON.parse(locationRoot) : null;

      if (locationRoot && String(locationRoot?.value) === String(id)) {
        localStorage.removeItem("selected-forms-location-root");
      }
    });
  };

  const handleAddDetail = (parent_id, body, callback = null) => {
    if (requestLoading) return;

    if (parent_id && body) {
      if (!body.name.trim()) {
        confirmAlert({
          customUI: ({ onClose }) => (
            <AlertModal
              buttons={[
                {
                  label: translate("okay"),
                  onClick: onClose,
                  classNames: "btn btn-primary",
                },
              ]}
              message={translate("fill_all_fields")}
            />
          ),
        });
        return false;
      }

      setIsSending(true);
      setRequestLoading(true);

      body = {
        name: body.name.trim(),
      };
      API.postData(`site_set/${parent_id}/details`, body)
        .then(({ data: res }) => {
          const { data } = res;

          const newItem = {
            id: data.id,
            name: data.name,
            hasDetail: false,
            set_id: parent_id,
          };

          const sets = [...locationSets];

          const index = sets.findIndex((item) => item.id === parent_id);

          if (index > -1) {
            sets[index].subTree = [...sets[index].subTree, newItem];
            setLocationSets(sets);
          }

          setIsSending(false);

          if (callback) callback(newItem);
        })
        .catch(() => {
          setIsSending(false);
        });
    }
  };

  return (
    <div className="LocationSets">
      <Helmet>
        <title>
          {translate("page_titles.project-settings-location-sets")} -{" "}
          {translate("page_titles.project-settings")}
        </title>
      </Helmet>
      <div className="row">
        <div className="col-12 d-flex">
          <div
            className="d-flex align-items-center mb-4"
            role="button"
            onClick={() =>
              project?.id
                ? setShowModal(true)
                : alert(translate("select_a_project_alert"))
            }
          >
            <BiPlus className="fs-1 bg-primary text-white rounded-circle" />
            <span className="ms-2">
              {translate(
                "project_settings.location_sets.create_new_set_button"
              )}
            </span>
          </div>
        </div>
      </div>
      {project?.id ? (
        <>
          <div className="row">
            <div className="col-12">
              <Sets
                sets={locationSets}
                setsLoading={isLoading}
                handleDelete={onDelete}
                handleAddDetail={handleAddDetail}
                type="LOCATION_SET"
              />
            </div>
          </div>

          <Modal
            title={translate("project_settings.location_sets.create_new_set")}
            positiveButtonNode={translate("save")}
            negativeButtonNode={translate("cancel")}
            positiveButtonHandler={handleSubmit}
            modalSize="lg"
            show={showModal}
            setShow={(show) => {
              setShowModal(show);
            }}
            disableButtons={isSending}
          >
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <Input
                  type="text"
                  name="locationSetName"
                  title={translate("project_settings.location_sets.set_name")}
                  placeholder={translate(
                    "project_settings.location_sets.set_name_placeholder"
                  )}
                  value={locationSetName}
                  onChange={(e) => setLocationSetName(e.target.value)}
                />
              </div>
            </form>
          </Modal>
        </>
      ) : (
        ``
      )}
    </div>
  );
};
export default LocationSets;
